import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import React, {useEffect, useState} from 'react';
import {t} from 'i18next';
import {ReactComponent as DownloadIcon} from '../../../assests/svg/download-icon.svg';
import AurButton from '../../common/aur-button/AurButton';
import AurLoadingIndicator from '../../common/aur-loading-indicator/AurLoadingIndictor';
import FamilyDateMap from '../../common/aur-cal-heatmap/FamilyDateMap';
import {downloadFamilyDateCsv} from '../../../api/EcaManagementService';
import {useSelector} from 'react-redux';
import {RootState} from '../../../redux/store';
import {
  downloadResponseAsCSV,
  getProjectReportFileName,
  getUTCDateTimeForDisplay,
} from '../../../utils/utils';
import {
  GA_CLICK_TYPES,
  GA_EVENT_NAMES,
  GA_FILE_EXTENTIONS,
  GA_REGIONS,
  GA_SUBPAGE_TYPE,
} from '../../../constants/analyticsConstants';
import {useAnalytics} from '../../../hooks/useAnalytics';
import {AurLightTooltip} from '../../common/aur-light-tooltip/aur-light-tooltip';

interface FamilyDateTypesDialogProps {
  isOpen: boolean;
  onClose: () => void;
  projectId: string | null | undefined;
  loadFileId?: number;
  projectName?: string | null;
}

//commenting this code as this is unused
// interface DocType {
//   docTypeId: string;
//   name: string;
//   documentCount: number;
// }

// // Define the type
// interface ProcessedDateRange {
//   startDate: string;
//   totalMonths: number;
//   noOfYears: number;
// }

const FamilyDateDialog: React.FC<FamilyDateTypesDialogProps> = ({
  isOpen,
  onClose,
  projectId,
  loadFileId,
  projectName,
}) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('md'));
  const {trackGAEvent} = useAnalytics();

  const [loading] = useState(false);
  //commenting this code as this is unused
  // const [isFailedToLoadContent, setIsFailedToLoadContent] = useState(false);
  // const [familyDateData, setFamilyDateData] = useState<Array<{ date: string; value: number }>>([]);
  // // Use a consistent type (null initially, updated to an object after fetching)
  // const [dateRange, setDateRange] = useState<ProcessedDateRange | null>(null);
  const [startDate, setStartDate] = useState<string | null>(null);
  const [endDate, setEndDate] = useState<string | null>(null);

  const clientId = useSelector(
    (state: RootState) => state.selectedClient
  ).clientId; //commenting this code as this is unused
  // const [currentYear, setCurrentYear] = useState(2025);
  // const years = Array.from({ length: 6 }, (_, i) => currentYear + i); // Generate a list of years from 2015 to 2034
  const [isClearAllLoading, setIsClearAllLoading] = useState<boolean>(false);

  // Callback to receive start & end date from FamilyDateMap

  const startDateUpdated = startDate
    ? startDate.split('T')[0].replace(/-/g, '/')
    : null;
  const endDateUpdated = endDate
    ? endDate.split('T')[0].replace(/-/g, '/')
    : null;
  const handleDateRangeChange = (newStartDate: string, newEndDate: string) => {
    setStartDate(newStartDate);
    setEndDate(newEndDate);
  };

  useEffect(() => {
    if (isOpen) {
      handleDownloadfamilyReportCsv();
    }
  }, []);

  const handleDownloadfamilyReportCsv = async () => {
    setIsClearAllLoading(true);

    let paramString = `clientId=${clientId}&projectId=${projectId}&startDate=${startDateUpdated}&endDate=${endDateUpdated}`;
    if (loadFileId) {
      paramString += `&loadFilesId=${loadFileId}`;
    }
    try {
      const response = await downloadFamilyDateCsv(paramString);
      downloadResponseAsCSV(
        response,
        getProjectReportFileName(
          projectName,
          t('ecaHomepageModals.ecaBottom.familyDate')
        )
      );
    } catch (e) {
      console.log(e);
    } finally {
      setIsClearAllLoading(false);
      trackGAEvent({
        eventName: GA_EVENT_NAMES.FILE_DOWNLOAD,
        file_extension: GA_FILE_EXTENTIONS.CSV,
        click: {
          name: t('gaTracking.global.downloadReport'),
          region: GA_REGIONS.MODAL,
          type: GA_CLICK_TYPES.BUTTON,
          action: t('gaTracking.global.downloadedReport'),
        },
        subPage: {
          subpage_name: t('gaTracking.ecaHomePage.subpageWidget', {
            label: t('gaTracking.ecaHomePage.familyDate'),
          }),
          subpage_type: GA_SUBPAGE_TYPE.MODAL,
        },
      });
    }
  };

  const renderModalHeader = () => (
    <Typography className="family-title typo-text subhead1_size lightWhite-color" id="family-date-modal-title">
      {t('ecaHomepageModals.ecaBottom.familyDate')}
    </Typography>
  );

  const renderHeaderActions = () => (
    <>
      <Box className="header-alignment">
        <div className="modal-count-header modal-family-header">
          <Typography className="typo-text display3_size lightWhite-color">
            {startDate && endDate
              ? `${startDateUpdated} - ${endDateUpdated}`
              : 'Loading...'}
          </Typography>
        </div>
        <Box>
          <AurButton
            data-testid="export-button"
            onClick={handleDownloadfamilyReportCsv}
            startIcon={
              isClearAllLoading ? (
                <div className="color-loader">
                  <AurLoadingIndicator
                    size={16}
                    className="progress-icon"
                    aria-hidden="true"
                  />
                </div>
              ) : (
                <DownloadIcon />
              )
            }
            type="continue"
            size="Medium"
            label={t('common.downloadReport')}
            classNames="typo-text buttonMedium_size"
          />
        </Box>
      </Box>
      <Box className="modal-family-subheader">
        <Typography className="typo-text label1_size body3_size">
          <span>{`${t('common.dataCurrentAsOf')} `}</span>
          <AurLightTooltip title={t('common.yearMonthDate')}>
            <span>{getUTCDateTimeForDisplay(new Date())}</span>
          </AurLightTooltip>
        </Typography>
      </Box>
    </>
  );
  const renderTable = () => (
    <>
      <FamilyDateMap
        totalMonths={12}
        isOpen={isOpen}
        projectId={projectId}
        onDateRangeChange={handleDateRangeChange}
        enableYearNav={true}
      />
    </>
  );

  const renderDialogActions = () => (
    <DialogActions
      className="footer-action-container remove-pagination-status"
      style={{
        background: '#fff',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
      }}
    >
      {/* Legend Section */}
      <div
        id="legend"
        className="legend"
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '8px',
        }}
      >
        {/* Legend Colors */}
        <div className="legend-colors">
          <div className="legend-cell legend-color"></div>
          <div className="legend-cell legend-color1"></div>
          <div className="legend-cell legend-color2"></div>
          <div className="legend-cell legend-color3"></div>
          <div className="legend-cell legend-color4"></div>
          <div className="legend-cell legend-color5"></div>
          <div className="legend-cell legend-color6"></div>
        </div>

        {/* Legend Labels */}
        <div className="legend-labels typo-text label3_size">
          <div>0</div>
          <div>1-100</div>
          <div>100-500</div>
          <div>500-1k</div>
          <div>1k-5k</div>
          <div>5k-10k</div>
          <div>10k+</div>
        </div>
      </div>

      {/* Footer Block */}
      <div className="footer-block">
        <Stack>
          <AurButton
            onClick={onClose}
            type="exit"
            size="Medium"
            label={t('ecaHomepageModals.docTypeModal.closeText')}
            classNames="pagination-cancel-button updateColor"
            dataTestId="pagination-cancel-button"
          />
        </Stack>
      </div>
    </DialogActions>
  );

  return (
    <Dialog
      fullScreen={fullScreen}
      open={isOpen}
      onClose={onClose}
      PaperProps={{className: 'evidence-source-dialog'}}
      maxWidth={false}
      className="model-content"
      aria-labelledby='family-date-modal-title'
    >
      <div className="eca-model-header family-date-h">
        {renderModalHeader()}
        {renderHeaderActions()}
      </div>
      <DialogContent className="model-content" id="eca-table-content">
        {renderTable()}
      </DialogContent>
      {renderDialogActions()}
      <AurLoadingIndicator overlay={true} isOpen={loading} />
    </Dialog>
  );
};

export default FamilyDateDialog;
